import { useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  BookOpen,
  Calculator,
  ClipboardList,
  Home,
  MessageCircle,
  Settings,
  UsersRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCity } from "@/contexts/CityContext";
import { useBoardHome, useBoardMe } from "@/lib/board";
import { useMessagesUnread } from "@/lib/messages";
import "@/styles/board.css";
import "@/styles/messages.css";
import "@/styles/board-premium.css";
import { PulseBoardMark } from "./PulseBoardMark";

const NAV_ITEMS = [
  { to: "/board", label: "Board", icon: Home, end: true },
  { to: "/board/learn", label: "5 Minute CPD", icon: BookOpen },
  { to: "/board/referrals", label: "Referrals", icon: ClipboardList },
  { to: "/board/resources", label: "Tools", icon: Calculator },
  {
    to: "/board/rounds",
    label: "Rounds",
    icon: UsersRound,
    badge: "roundsUnread",
  },
  {
    to: "/board/messages",
    label: "Messages",
    icon: MessageCircle,
    badge: "messagesUnread",
  },
  { to: "/board/settings", label: "Settings", icon: Settings },
] as const;

export function BoardShell() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  const { city } = useCity();
  const { user, logout } = useAuth();
  const member = useBoardMe(city.slug);
  const home = useBoardHome(city.slug);
  const messagesUnreadQuery = useMessagesUnread(city.slug);
  const memberName = member.data?.displayName || user?.name || "PulseBoard member";
  const roundsUnread = home.data?.beforeYouLeave.roundsUnread ?? 0;
  const messagesUnread = messagesUnreadQuery.data?.unread ?? 0;

  return (
    <div className="pb-board-shell">
      <button className="pb-board-skip" type="button" onClick={() => document.getElementById("board-main")?.focus()}>Skip to content</button>
      <aside className="pb-board-rail">
        <NavLink to="/board" end className="pb-board-brand" aria-label="PulseBoard Board home">
          <span className="pb-board-brand__mark"><PulseBoardMark /></span>
          <span><span className="pb-board-wordmark">Pulse<span className="pb-wordmark-light">Board</span></span><small>THE CLINICIAN WORKSPACE</small></span>
        </NavLink>
        <BoardNav roundsUnread={roundsUnread} messagesUnread={messagesUnread} />
        <p className="pb-board-rail__privacy">
          No patient identifiers. Referral drafts stay in your browser.
        </p>
      </aside>

      <div className="pb-board-workspace">
        <header className="pb-board-topbar">
          <div>
            <strong>{memberName}</strong>
            <span>{city.name} · {city.state}</span>
          </div>
          <button
            type="button"
            onClick={() => void logout({ redirectUri: window.location.origin })}
          >
            Sign out
          </button>
        </header>
        <main id="board-main" tabIndex={-1} className="pb-board-main">
          <Outlet />
        </main>
      </div>

      <nav className="pb-board-bottom" aria-label="Board areas">
        <BoardNav mobile roundsUnread={roundsUnread} messagesUnread={messagesUnread} />
      </nav>
    </div>
  );
}

function BoardNav({
  mobile = false,
  roundsUnread,
  messagesUnread,
}: {
  mobile?: boolean;
  roundsUnread: number;
  messagesUnread: number;
}) {
  return (
    <div className={mobile ? "pb-board-nav pb-board-nav--mobile" : "pb-board-nav"}>
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const badge = "badge" in item
          ? item.badge === "roundsUnread"
            ? roundsUnread
            : messagesUnread
          : null;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={"end" in item ? item.end : false}
            className={({ isActive }) => (isActive ? "is-active" : undefined)}
          >
            <Icon size={17} aria-hidden="true" />
            <span>{item.label}</span>
            {"badge" in item ? (
              <span
                className="pb-board-nav__badge"
                aria-label={
                  item.badge === "roundsUnread"
                    ? `${badge} unread Rounds ${badge === 1 ? "thread" : "threads"}`
                    : `${badge} unread ${badge === 1 ? "message" : "messages"}`
                }
              >
                {badge}
              </span>
            ) : null}
          </NavLink>
        );
      })}
    </div>
  );
}
